import { useMutation } from "@tanstack/react-query";
import { publishLesson, unpublishLesson } from "api/services/lesson-service";
import { useAuthContext } from "hooks/useAuthContext";
import useRefreshToken from "hooks/useRefreshToken";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { RoutePaths } from "constants/RoutePaths";

interface SetLessonDraftStatusParams {
    lessonId: string;
    isDraft: boolean;
}

export const useSetLessonDraftStatus = () => {
    const { userState } = useAuthContext();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { refreshToken } = useRefreshToken();

    const mutation = useMutation({
        mutationFn: async ({ lessonId, isDraft }: SetLessonDraftStatusParams) => {
            const workspaceId = userState.user.selectedWorkspace.workspaceId;
            if (!workspaceId) throw new Error("No workspace selected");

            const request = { lessonId, workspaceId };
            const changeDraftStatus = () => isDraft ? unpublishLesson(request) : publishLesson(request);

            let response;
            try {
                response = await changeDraftStatus();
            } catch (e: any) {
                if (e?.response?.status !== 401) {
                    throw e;
                }

                const refreshResponse = await refreshToken();
                if (!refreshResponse.success) {
                    window.location.href = RoutePaths.LOGIN;
                    throw e;
                }

                response = await changeDraftStatus();
            }

            if (!response?.success) {
                throw new Error(response?.message ?? (isDraft ? "unpublishLesson.error" : "publishLesson.error"));
            }

            return response;
        },
        onError: (_error, { isDraft }) => {
            enqueueSnackbar(t(isDraft ? "unpublishLesson.error" : "publishLesson.error"), { variant: "error" });
        },
    });

    const setLessonDraftStatus = async (params: SetLessonDraftStatusParams): Promise<boolean> => {
        try {
            await mutation.mutateAsync(params);
            return true;
        } catch {
            return false;
        }
    };

    return { setLessonDraftStatus };
};
